import { FormGroupHeader } from "@/components/shared";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useState, useCallback } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useQuery } from "@apollo/client/react";
import {
  GetSheetAgentRunByIdQuery,
  GetSheetAgentRunByIdQueryVariables,
  GetSheetAgentRunByIdDocument,
} from "@/graphql/__generated__/graphql";
import CalculationsInfoComponent from "./CalculationsInfoComponent/CalculationsInfoComponent";
import SheetTasksContainerComponent from "./SheetTasksContainerComponent";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import useFillSheetMutationHook from "@/hooks/mutations/useFillSheetMutation";
import DownloadUrlDialog from "../DownloadUrlDialog";
import SheetAgentHelpDialog from "../SheetAgentHelpDialog/SheetAgentHelpDialog";
import { useTenderPermissionsData } from "@/hooks/cache/useTenderPermissionsData";

function SheetAgentRunPage() {
  const { t } = useTranslation();
  const { sheetAgentRunId } = useParams<{ sheetAgentRunId: string }>();
  const { tenderID } = useParams<{ tenderID: string }>();
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadUrlsFromMutation, setDownloadUrlsFromMutation] = useState<string | null | undefined>(undefined);
  const { fillSheetMutation, loading: fillSheetLoading } = useFillSheetMutationHook();
  const { canManageSheetAgent } = useTenderPermissionsData(Number(tenderID));

  // Fetch sheet agent run data with tasks
  const { data, loading } = useQuery<
    GetSheetAgentRunByIdQuery,
    GetSheetAgentRunByIdQueryVariables
  >(GetSheetAgentRunByIdDocument, {
    variables: {
      getSheetAgentRunByIdId: sheetAgentRunId ? Number(sheetAgentRunId) : 0,
    },
    skip: !sheetAgentRunId || sheetAgentRunId === "0",
    fetchPolicy: "cache-and-network",
  });

  // Get tenderId, tasks, and documents from data
  const tenderId = data?.getSheetAgentRunById?.tenderId;
  const tasks = data?.getSheetAgentRunById?.tasks ?? [];
  const documents = data?.getSheetAgentRunById?.documents ?? [];

  const handleFillSheet = useCallback(async () => {
    if (!sheetAgentRunId) {
      return;
    }

    const result = await fillSheetMutation({
      variables: {
        runId: Number(sheetAgentRunId),
      },
    });
    // Extract downloadUrls from mutation response
    const downloadUrls = result?.data?.fillSheet?.downloadUrls;
    if (downloadUrls) {
      setDownloadUrlsFromMutation(downloadUrls);
      // Open the download dialog
      setDownloadDialogOpen(true);
    }
  }, [sheetAgentRunId, fillSheetMutation]);

  const buttonConfig: ActionButtonConfig[] = canManageSheetAgent
    ? [
      {
        text: t("edittender.sheetagent.runpage.fillsheet"),
        variant: "default",
        actionFn: handleFillSheet,
        disabled: fillSheetLoading,
        loading: fillSheetLoading,
      },
    ]
    : [];

  // Show loading state
  if (loading) {
    return (
      <div>
        <FormGroupHeader
          title={t("edittender.sheetagent.title")}
          className="pr-4 pl-2"
          showBackButton={true}
        />
        <div className="flex items-center justify-center p-8 h-[calc(100dvh-170px)]">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div>
      <FormGroupHeader
        title={t("edittender.sheetagent.title")}
        className="pr-4 pl-2"
        showBackButton={true}
        actionButtons={buttonConfig}
      >
        <div className="flex items-center gap-2">
          <SheetAgentHelpDialog />
          <DownloadUrlDialog
            downloadUrls={downloadUrlsFromMutation ?? data?.getSheetAgentRunById?.downloadUrls}
            open={downloadDialogOpen}
            onOpenChange={setDownloadDialogOpen}
          />
        </div>
      </FormGroupHeader>
      <div className="h-[calc(100dvh-170px)] pr-4 pl-2">
        <ResizablePanelGroup orientation="horizontal" className="h-full">
          <ResizablePanel defaultSize={50} minSize={30} className="flex flex-col min-h-0">
            {/* Task Container */}
            <SheetTasksContainerComponent
              tenderId={tenderId}
              tasks={tasks}
              documents={documents}
            />
          </ResizablePanel>
          <ResizableHandle withHandle className="mx-4" />
          <ResizablePanel defaultSize={50} minSize={30} className="flex flex-col min-h-0">
            {/* Calculations Info Container */}
            <CalculationsInfoComponent />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </div>
  );
}

export default SheetAgentRunPage;
